import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  styled,
  Tabs,
  Tab,
  TextField,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, PlayArrow as PlayIcon } from '@mui/icons-material';
import { Movie as VideoIcon, Image as ImageIcon } from '@mui/icons-material';
import { post, get, put, del } from '../../utility/fetchWeb';
import notification from '../../utility/notification';
import CategoriesManagement from './CategoriesManagement';
import RoleSpecialistManager from '../Layout/SpecialistManager';
import ArticleModal from '../Layout/ArticleModal';
import Tooltip from '@mui/material/Tooltip'; // Import Tooltip for hover messages

const VisuallyHiddenInput = styled('input')`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  white-space: nowrap;
  width: 1px;
`;

const AdvertManagement = () => {
  const [adverts, setAdverts] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedAdvert, setSelectedAdvert] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [categories, setCategories] = useState([]);
  const [fileType, setFileType] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [articles, setArticles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [articleToDelete, setArticleToDelete] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [isToggling, setIsToggling] = useState(false);

  // New states for article editing
  const [isArticleModalOpen, setIsArticleModalOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // New state for pagination and filtering
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalArticles, setTotalArticles] = useState(0);
  const [loadingArticles, setLoadingArticles] = useState(false);

  // New state for pagination error handling
  const [pageChangeError, setPageChangeError] = useState(false);

  // Add state to track if we have more articles
  const [hasMoreArticles, setHasMoreArticles] = useState(true);

  // New state for individual article toggle loading
  const [togglingArticles, setTogglingArticles] = useState(new Set());

  const fetchCategories = async () => {
    try {
      const response = await get('/categories');
      if (response?.code === 200) setCategories(response?.data);
    } catch {
      notification({ type: 'error', message: 'Failed to fetch categories' });
    }
  };

  const fetchAdverts = async () => {
    try {
      const response = await get('/Adverts/all-adverts');
      if (response) setAdverts(response);
    } catch {
      notification({ type: 'error', message: 'Failed to fetch adverts' });
    }
  };

  const fetchArticles = async (page = 1, categoryId = '', featured = false) => {
    setLoadingArticles(true);
    setPageChangeError(false); // Reset error state
    try {
      let endpoint = '/Articles';
      const params = new URLSearchParams();

      if (categoryId) params.append('categoryId', categoryId);
      if (featured) params.append('featuredOnly', 'true');
      params.append('page', page.toString());
      params.append('pageSize', pageSize.toString());

      if (params.toString()) {
        endpoint += `?${params.toString()}`;
      }

      console.log(`Fetching articles - Page: ${page}, PageSize: ${pageSize}, Category: ${categoryId}, Featured: ${featured}`);

      const response = await get(endpoint);
      if (response?.code === 200) {
        const articles = response.data?.items || response.data || [];
        setArticles(articles);
        setTotalArticles(response.data?.totalCount || response.totalCount || articles.length || 0);
        
        // Check if we have more pages by comparing fetched count with page size
        const hasMore = articles.length === pageSize;
        setHasMoreArticles(hasMore);
        
        console.log(`Fetched ${articles.length} articles, Has more: ${hasMore}`);
      } else {
        throw new Error('Failed to fetch articles');
      }
    } catch (error) {
      console.error('Error fetching articles:', error);
      setPageChangeError(true);
      notification({ type: 'error', message: 'Failed to fetch articles' });
    } finally {
      setLoadingArticles(false);
    }
  };

  useEffect(() => {
    fetchAdverts();
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchArticles(currentPage, selectedCategoryId, featuredOnly);
  }, [currentPage, selectedCategoryId, featuredOnly, pageSize]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const type = file.type.startsWith('image/') ? 'image' : 'video';
    setFileType(type);
    setSelectedFile(file);
    const previewUrl = URL.createObjectURL(file);
    setFilePreview(previewUrl);
    return () => URL.revokeObjectURL(previewUrl);
  };

  const handleSubmit = async () => {
    if (!selectedFile) return notification({ type: 'error', message: 'Please select media' });
    setIsLoading(true);
    const formData = new FormData();
    formData.append('Image', selectedFile);
    try {
      const response = selectedAdvert
        ? await put(`/Adverts/${selectedAdvert.id}`, formData)
        : await post('/Adverts', formData);
      if (response.status === 1) {
        notification({ type: 'success', message: selectedAdvert ? 'Advert updated' : 'Advert posted' });
        fetchAdverts();
        setIsDialogOpen(false);
        setSelectedAdvert(null);
        setSelectedFile(null);
      }
    } catch {
      notification({ type: 'error', message: selectedAdvert ? 'Update failed' : 'Post failed' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (id, checked) => {
    // Find the advert object for this row
    const ad = adverts.find(a => a.id === id);
    if (!ad) return;

    // Build a FormData payload: status + existing image URL
    const formData = new FormData();
    formData.append('Status', checked ? 1 : 0);

    formData.append('Image', ad.imageUrl);

    try {
      const res = await put(`/Adverts/${id}`, formData);
      if (res?.status === 0 || res?.status === 1) {
        notification({ type: 'success', message: 'Status updated' });
        fetchAdverts();
      } else {
        throw new Error();
      }
    } catch (err) {
      notification({ type: 'error', message: 'Failed to update status' });
    }
  };


  const handleDelete = async (id) => {
    try {
      await del(`/Adverts/${id}`);
      notification({ type: 'success', message: 'Advert deleted' });
      fetchAdverts();
    } catch {
      notification({ type: 'error', message: 'Delete failed' });
    }
  };

  const handleArticleDelete = async () => {
    if (!articleToDelete) return;
    try {
      await del(`/articles/${articleToDelete}`);
      notification({ type: 'success', message: 'Article deleted' });
      fetchArticles();
    } catch {
      notification({ type: 'error', message: 'Failed to delete article' });
    } finally {
      setConfirmDialogOpen(false);
      setArticleToDelete(null);
    }
  };

  const handleTabChange = (_, value) => setActiveTab(value);

  // Handle filter changes
  const handleCategoryChange = (categoryId) => {
    console.log('Category changed to:', categoryId);
    setSelectedCategoryId(categoryId);
    setCurrentPage(1); // Reset to first page when filter changes
    fetchArticles(1, categoryId, featuredOnly); // Immediately fetch with new filter
  };

  const handleFeaturedToggle = (featured) => {
    setFeaturedOnly(featured);
    setCurrentPage(1); // Reset to first page when filter changes
    fetchArticles(1, selectedCategoryId, featured); // Immediately fetch with new filter
  };

  const handlePageChange = async (newPage) => {
    console.log(`Page changing from ${currentPage} to ${newPage}`);
    setCurrentPage(newPage);
    
    try {
      await fetchArticles(newPage, selectedCategoryId, featuredOnly);
    } catch (error) {
      console.error('Error in handlePageChange:', error);
      // Reset to previous page if fetch fails
      setCurrentPage(currentPage);
      setPageChangeError(true);
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when page size changes
    fetchArticles(1, selectedCategoryId, featuredOnly); // Immediately fetch with new page size
  };

  // Clear all filters
  const clearFilters = () => {
    console.log('Clearing all filters');
    setSelectedCategoryId('');
    setFeaturedOnly(false);
    setCurrentPage(1);
    setSearchQuery(''); // Also clear search query
    fetchArticles(1, '', false); // Immediately fetch with cleared filters
  };

  // Filter articles by search query locally
  const filteredArticles = articles.filter(article =>
    article.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleArticleToggle = async (article, field) => {
    const toggleKey = `${article.id}-${field}`;
    setTogglingArticles(prev => new Set([...prev, toggleKey]));

    const payload = {
      title: article.title,
      shortDescription: article.shortDescription,
      fullDescription: article.fullDescription,
      featuredImagePath: article.featuredImagePath,
      categoryId: article.categoryId,
      [field]: !article[field], // Toggle the specific field (isPublished or isFeatured)
    };

    try {
      const formData = new FormData();
      Object.keys(payload).forEach((key) => {
        formData.append(key, payload[key]);
      });

      await put(`/Articles/${article.id}`, formData);
      notification({
        type: 'success',
        message: `Article "${article.title}" ${field === 'isPublished' ? 'publication status' : 'featured status'} updated successfully`,
      });
      fetchArticles(currentPage, selectedCategoryId, featuredOnly); // Refresh the list
    } catch (error) {
      notification({
        type: 'error',
        message: `Failed to update ${field === 'isPublished' ? 'publication status' : 'featured status'} for article "${article.title}"`,
      });
    } finally {
      setTogglingArticles(prev => {
        const newSet = new Set(prev);
        newSet.delete(toggleKey);
        return newSet;
      });
    }
  };

  const handleArticleEdit = (article) => {
    setEditingArticle(article);
    setIsArticleModalOpen(true);
  };

  const handleArticleSubmit = async (data) => {
    const isEdit = editingArticle !== null;
    setIsUpdating(isEdit);

    const formData = new FormData();
    formData.append('Title', data.Title);
    formData.append('ShortDescription', data.ShortDescription);
    formData.append('FullDescription', data.FullDescription);
    formData.append('CategoryId', data.CategoryId);
    formData.append('IsPublished', data.IsPublished);
    formData.append('IsFeatured', data.IsFeatured);
    if (data.FeaturedImage) {
      formData.append('FeaturedImage', data.FeaturedImage);
    }
    formData.append('UserId', sessionStorage.getItem('userId'));
    formData.append('UserType', 2);

    try {
      let res;
      if (isEdit) {
        res = await put(`/Articles/${editingArticle.id}`, formData);
      } else {
        res = await post('/articles', formData);
      }

      if (res.status === 'success' || res.code === 200) {
        notification({
          message: `Article ${isEdit ? 'updated' : 'created'} successfully`,
          type: 'success'
        });

        handleArticleModalClose();
        fetchArticles(currentPage, selectedCategoryId, featuredOnly);
      } else {
        notification({ message: res?.message || 'Error', type: 'error' });
      }
    } catch (error) {
      console.error('Error submitting article:', error);
      notification({ message: 'An error occurred', type: 'error' });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleArticleModalClose = () => {
    setIsArticleModalOpen(false);
    setEditingArticle(null);
    setIsUpdating(false);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
          <Tab label="Advertisements" />
          <Tab label="Categories" />
          <Tab label="Article Management" />
          <Tab label="Role Specialists" />
        </Tabs>
      </Box>

      {activeTab === 0 ? (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
            <Typography variant="h5">Advert Management</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsDialogOpen(true)}>
              New Advert
            </Button>
          </Box>

          <Paper sx={{ borderRadius: 2 }}>
            <List>
              {adverts?.map((ad) => (
                <ListItem key={ad.id} divider>
                  {ad.imageUrl.endsWith('.mp4') ? (
                    <Box sx={{ position: 'relative', width: 100, height: 60, mr: 2 }}>
                      <video
                        src={`${process.env.REACT_APP_MEDIA_URL}${ad.imageUrl}`}
                        poster={`${process.env.REACT_APP_MEDIA_URL}${ad.imageUrl}?vframe`}
                        muted
                        width="100%"
                        height="100%"
                        style={{ objectFit: 'cover', borderRadius: 4 }}
                        onMouseEnter={(e) => e.currentTarget.play()}
                        onMouseLeave={(e) => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }}
                      />
                      <PlayIcon sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: 'white', opacity: 0.7 }} />
                    </Box>
                  ) : (
                    <Box
                      component="img"
                      src={`${process.env.REACT_APP_MEDIA_URL}${ad.imageUrl}`}
                      alt="thumb"
                      sx={{ width: 100, height: 60, objectFit: 'cover', borderRadius: 1, mr: 2 }}
                    />
                  )}

                  <ListItemText
                    primary={`Advert #${ad.id}`}
                    secondary={new Date(ad.dateCreated).toLocaleDateString()}
                  />

                  <ListItemSecondaryAction>
                    <IconButton onClick={() => { setSelectedAdvert(ad); setIsDialogOpen(true); }}>
                      <EditIcon />
                    </IconButton>
                    <Switch checked={ad.status === 1} onChange={(e) => handleStatusChange(ad.id, e.target.checked)} />
                    <IconButton onClick={() => handleDelete(ad.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Paper>

          <Dialog open={isDialogOpen} onClose={() => { setIsDialogOpen(false); setSelectedAdvert(null); setSelectedFile(null); }}>
            <DialogTitle>{selectedAdvert ? 'Edit Advertisement' : 'Add New Advertisement'}</DialogTitle>
            <DialogContent>
              {selectedAdvert && (
                <Box sx={{ width: '100%', height: 200, mb: 2, mt: 2, position: 'relative', borderRadius: 1, overflow: 'hidden' }}>
                  {selectedAdvert.imageUrl.endsWith('.mp4') ? (
                    <video
                      src={`${process.env.REACT_APP_MEDIA_URL}${selectedAdvert.imageUrl}`}
                      controls
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <Box
                      component="img"
                      src={`${process.env.REACT_APP_MEDIA_URL}${selectedAdvert.imageUrl}`}
                      alt="Current advertisement"
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  )}
                </Box>
              )}

              {filePreview && !selectedAdvert && (
                <Box sx={{ width: '100%', height: 200, mb: 2, position: 'relative', borderRadius: 1, overflow: 'hidden' }}>
                  {fileType === 'video' ? (
                    <video
                      src={filePreview}
                      controls
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <Box
                      component="img"
                      src={filePreview}
                      alt="Preview"
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  )}
                </Box>
              )}

              <Button
                component="label"
                variant="outlined"
                startIcon={selectedFile ? (fileType === 'video' ? <VideoIcon /> : <ImageIcon />) : null}
                sx={{ mt: 2, height: 56, display: 'flex', justifyContent: 'flex-start' }}
                fullWidth
              >
                {selectedFile ? selectedFile.name : 'Choose Media (Image or Video)'}
                <VisuallyHiddenInput type="file" onChange={handleFileChange} accept="image/*,video/mp4,video/*" />
              </Button>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { setIsDialogOpen(false); setSelectedAdvert(null); setSelectedFile(null); }}>Cancel</Button>
              <Button onClick={handleSubmit} variant="contained" disabled={!selectedFile || isLoading}>
                {isLoading ? 'Uploading...' : selectedAdvert ? 'Update' : 'Upload'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : activeTab === 1 ? (
        <CategoriesManagement categories={categories} fetchCategories={fetchCategories} />
      ) : activeTab === 2 ? (
        <Box>
          <Typography variant="h5" sx={{ mb: 2 }}>Articles</Typography>

          {/* Search and Filters */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Search articles by title"
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                select
                label="Category Filter"
                value={selectedCategoryId}
                onChange={(e) => handleCategoryChange(e.target.value)}
                sx={{ minWidth: 200 }}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </TextField>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">Featured Only:</Typography>
                <Switch
                  checked={featuredOnly}
                  onChange={(e) => handleFeaturedToggle(e.target.checked)}
                />
              </Box>

              <TextField
                select
                label="Page Size"
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                sx={{ minWidth: 120 }}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </TextField>

              <Button variant="outlined" onClick={clearFilters}>
                Clear Filters
              </Button>
            </Box>
          </Box>

          {/* Loading State - Enhanced with page loading indicator */}
          {loadingArticles && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress size={24} sx={{ mr: 2 }} />
              <Typography variant="body2">
                Loading articles for page {currentPage}...
              </Typography>
            </Box>
          )}

          {/* Articles List */}
          <Paper sx={{ borderRadius: 2 }}>
            <List>
              {filteredArticles.map(article => {
                const isPublishedToggling = togglingArticles.has(`${article.id}-isPublished`);
                const isFeaturedToggling = togglingArticles.has(`${article.id}-isFeatured`);
                
                return (
                  <ListItem key={article.id} divider>
                    <Box
                      component="img"
                      src={`${process.env.REACT_APP_MEDIA_URL}${article.featuredImagePath}`}
                      alt="thumb"
                      sx={{ width: 100, height: 60, objectFit: 'cover', borderRadius: 1, mr: 2 }}
                    />
                    <ListItemText
                      primary={article.title}
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(article.publishedDate).toLocaleDateString()}
                          </Typography>
                          {article.category && (
                            <Typography variant="caption" color="primary">
                              {article.category.name}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Edit Article">
                        <IconButton
                          onClick={() => handleArticleEdit(article)}
                          sx={{ mr: 1 }}
                          disabled={loadingArticles}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Toggle Published Status">
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', position: 'relative' }}>
                          <Switch
                            checked={article.isPublished}
                            onChange={() => handleArticleToggle(article, 'isPublished')}
                            disabled={isPublishedToggling || loadingArticles}
                            color="success"
                          />
                          {isPublishedToggling && (
                            <CircularProgress 
                              size={16} 
                              sx={{ 
                                position: 'absolute', 
                                top: '50%', 
                                left: '50%', 
                                transform: 'translate(-50%, -50%)' 
                              }} 
                            />
                          )}
                        </Box>
                      </Tooltip>
                      
                      <Tooltip title="Toggle Featured Status">
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', position: 'relative' }}>
                          <Switch
                            checked={article.isFeatured}
                            onChange={() => handleArticleToggle(article, 'isFeatured')}
                            disabled={isFeaturedToggling || loadingArticles}
                            color="warning"
                          />
                          {isFeaturedToggling && (
                            <CircularProgress 
                              size={16} 
                              sx={{ 
                                position: 'absolute', 
                                top: '50%', 
                                left: '50%', 
                                transform: 'translate(-50%, -50%)' 
                              }} 
                            />
                          )}
                        </Box>
                      </Tooltip>
                      
                      <IconButton
                        onClick={() => {
                          setArticleToDelete(article.id);
                          setConfirmDialogOpen(true);
                        }}
                        disabled={loadingArticles}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}

              {filteredArticles.length === 0 && !loadingArticles && (
                <ListItem>
                  <ListItemText
                    primary="No articles found"
                    secondary="Try adjusting your search or filter criteria"
                    sx={{ textAlign: 'center' }}
                  />
                </ListItem>
              )}
            </List>
          </Paper>

          {/* Error message when page change fails */}
          {pageChangeError && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <Typography variant="body2" color="error" sx={{ textAlign: 'center' }}>
                ⚠️ Unable to fetch more articles. Please try again or check your connection.
              </Typography>
            </Box>
          )}

          {/* Pagination - Enhanced with loading states */}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3, gap: 2 }}>
            <Button
              variant="outlined"
              disabled={currentPage === 1 || loadingArticles || pageChangeError}
              onClick={() => handlePageChange(1)}
              startIcon={loadingArticles && currentPage !== 1 ? <CircularProgress size={16} /> : null}
              sx={{ 
                ...(pageChangeError && { 
                  backgroundColor: 'grey.300', 
                  color: 'grey.500',
                  '&:disabled': {
                    backgroundColor: 'grey.300',
                    color: 'grey.500'
                  }
                })
              }}
            >
              First
            </Button>

            <Button
              variant="outlined"
              disabled={currentPage === 1 || loadingArticles || pageChangeError}
              onClick={() => handlePageChange(currentPage - 1)}
              startIcon={loadingArticles ? <CircularProgress size={16} /> : null}
              sx={{ 
                ...(pageChangeError && { 
                  backgroundColor: 'grey.300', 
                  color: 'grey.500',
                  '&:disabled': {
                    backgroundColor: 'grey.300',
                    color: 'grey.500'
                  }
                })
              }}
            >
              Previous
            </Button>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">
                Page {currentPage}
                {loadingArticles && (
                  <CircularProgress size={12} sx={{ ml: 1 }} />
                )}
              </Typography>
            </Box>

            <Button
              variant="outlined"
              disabled={!hasMoreArticles || loadingArticles || pageChangeError}
              onClick={() => handlePageChange(currentPage + 1)}
              endIcon={loadingArticles ? <CircularProgress size={16} /> : null}
              sx={{ 
                ...(pageChangeError && { 
                  backgroundColor: 'grey.300', 
                  color: 'grey.500',
                  '&:disabled': {
                    backgroundColor: 'grey.300',
                    color: 'grey.500'
                  }
                })
              }}
            >
              Next
            </Button>
          </Box>


          <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogContent>
              Are you sure you want to delete this article? This action cannot be undone.
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleArticleDelete} variant="contained" color="error">
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          {/* Article Modal */}
          <ArticleModal
            getArticles={() => fetchArticles(currentPage, selectedCategoryId, featuredOnly)}
            onSubmit={handleArticleSubmit}
            open={isArticleModalOpen}
            onClose={handleArticleModalClose}
            editingArticle={editingArticle}
            isLoading={isUpdating}
          />
        </Box>
      ) : activeTab === 3 ? (<RoleSpecialistManager embedded />) :
        (
          <Typography variant="h6" sx={{ textAlign: 'center', mt: 4 }}>
            Please select a tab to manage adverts, categories, or articles.
          </Typography>
        )}
    </Container>
  );
};



export default AdvertManagement;
